/**
 * Opt-in X attribution: link the X account on the user's Privy login to all of their Solana wallets
 * (POST /api/identity/link), so the site shows @handle next to them. If the Privy account has no X yet, Privy's
 * linkTwitter() runs first (an OAuth redirect); the watcher finishes the link when the user comes back.
 */
import { useLinkAccount, usePrivy } from '@privy-io/react-auth';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { create } from 'zustand';
import { api, ApiError } from '@/api/client';
import { identityKey } from '@/api/identity';
import { shortAddr } from '@/lib/format';
import { Button } from '@/ui/Button';
import { Dialog, DialogContent } from '@/ui/primitives';

const PENDING = 'raydius.linkX.pending';

const session = {
  get: () => {
    try {
      return sessionStorage.getItem(PENDING) === '1';
    } catch {
      return false;
    }
  },
  set: (on: boolean) => {
    try {
      if (on) sessionStorage.setItem(PENDING, '1');
      else sessionStorage.removeItem(PENDING);
    } catch {
      // storage blocked: the link then needs a second click after the X sign-in
    }
  },
};

interface XLinkState {
  busy: boolean;
  /** The server runs without Privy (403): hide X linking for this session. */
  off: boolean;
  set: (patch: Partial<Omit<XLinkState, 'set'>>) => void;
}

export const useXLinkState = create<XLinkState>()((set) => ({ busy: false, off: false, set: (patch) => set(patch) }));

let inflight: Promise<void> | null = null;

function postLink(qc: QueryClient, login: () => void, linkTwitter: () => void): Promise<void> {
  if (inflight) return inflight;
  session.set(false);
  const st = useXLinkState.getState();
  st.set({ busy: true });
  inflight = (async () => {
    try {
      const { items } = await api.linkIdentity();
      for (const i of items) qc.setQueryData(identityKey(i.wallet), i);
      void qc.invalidateQueries({ queryKey: ['identities'] });
      const handle = items[0]?.x.username;
      toast.success(handle ? `@${handle} now shows next to ${items.length === 1 ? 'your wallet' : `your ${items.length} wallets`}.` : 'X linked.');
    } catch (e) {
      if (!(e instanceof ApiError)) toast.error(e instanceof Error ? e.message : String(e));
      else if (e.code === 'unauthorized') login();
      else if (e.code === 'no_x_account') {
        session.set(true);
        linkTwitter();
      } else if (e.code === 'forbidden') {
        st.set({ off: true });
        toast.error('X linking is not available on this server.');
      } else if (e.code === 'unavailable') toast.error('X linking is unavailable right now. Try again later.');
      else if (e.code === 'not_found') toast.error('Raydius could not find your sign-in. Log out, log in again, then retry.');
      else if (e.code === 'rate_limited') toast.error(`Too many requests. Try again in ${e.retryAfterSec ?? 60} s.`);
      else toast.error(e.message);
    } finally {
      st.set({ busy: false });
      inflight = null;
    }
  })();
  return inflight;
}

export function useXLink() {
  const { authenticated, user, login } = usePrivy();
  const qc = useQueryClient();
  const { linkTwitter } = useLinkAccount();
  const busy = useXLinkState((s) => s.busy);
  const off = useXLinkState((s) => s.off);
  const link = async () => {
    if (!authenticated) return login();
    if (!user?.twitter?.username) {
      session.set(true);
      linkTwitter();
      return;
    }
    await postLink(qc, () => login(), linkTwitter);
  };
  const unlink = async () => {
    const st = useXLinkState.getState();
    st.set({ busy: true });
    try {
      await api.unlinkIdentity();
      await qc.invalidateQueries({ queryKey: ['identity'] });
      await qc.invalidateQueries({ queryKey: ['identities'] });
      toast.success('X unlinked. Your wallets no longer show a handle.');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'unauthorized') login();
      else if (e instanceof ApiError && e.code === 'forbidden') st.set({ off: true });
      else toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      st.set({ busy: false });
    }
  };
  return { link, unlink, busy, off };
}

/** Finishes a link after Privy's X sign-in (callback or redirect back). Mounted once, inside the PrivyProvider. */
export function XLinkWatcher() {
  const { ready, authenticated, user, login } = usePrivy();
  const qc = useQueryClient();
  const { linkTwitter } = useLinkAccount({
    onSuccess: ({ linkMethod }) => {
      if (linkMethod === 'twitter' && session.get()) void postLink(qc, () => login(), linkTwitter);
    },
    onError: () => session.set(false),
  });
  const handle = user?.twitter?.username;
  useEffect(() => {
    if (ready && authenticated && handle && session.get()) void postLink(qc, () => login(), linkTwitter);
  }, [ready, authenticated, handle, qc, login, linkTwitter]);
  return null;
}

/** Before linking: say what becomes public. */
export function LinkXDialog({ open, onOpenChange, wallets }: { open: boolean; onOpenChange: (o: boolean) => void; wallets: string[] }) {
  const { link, busy } = useXLink();
  const { user } = usePrivy();
  const handle = user?.twitter?.username;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Show your X handle" description="Optional. You can unlink at any time.">
        <div className="space-y-3 text-sm leading-6 text-fg-3">
          <p>
            Raydius will show {handle ? <b className="text-fg">@{handle}</b> : 'your X handle'} publicly next to every Solana wallet on your Raydius account
            {wallets.length ? <>: {wallets.map((w) => shortAddr(w, 4, 4)).join(', ')}</> : null}.
          </p>
          {!handle && <p>You will sign in to X first, then come back here.</p>}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="accent"
            loading={busy}
            onClick={() => {
              onOpenChange(false);
              void link();
            }}
          >
            {handle ? 'Link X' : 'Sign in to X'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
